import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema } from "@/lib/jsonld";

const Privacy = () => {
  const jsonLdSchemas = [
    generateWebPageSchema("Politique de confidentialité", seoConfig.privacy.description, "/privacy"),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Politique de confidentialité", path: "/privacy" }
    ])
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.privacy} jsonLd={jsonLdSchemas} />
      <Header />
      <main className="flex-1">
        {/* Hero Section - Titre principal */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] text-center">
              Politique de confidentialité
            </h1>
          </div>
        </section>

        {/* Contenu des articles */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* 1. Introduction */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4">
              1. Introduction
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif, marque exploitée par la société SEO FRENCH TOUCH LLC, attache une importance particulière à la protection des données personnelles et au respect de la vie privée de ses utilisateurs.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La présente politique de confidentialité a pour objectif d'informer, de manière claire et transparente, toute personne utilisant les services Unotif (ci-après les « Utilisateurs ») sur la manière dont leurs données personnelles sont collectées, utilisées, protégées et, le cas échéant, partagées.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              En accédant au site internet, à l'application web ou à tout service proposé par Unotif, vous acceptez les pratiques décrites dans la présente politique de confidentialité.
              Si vous n'acceptez pas ces conditions, vous devez cesser immédiatement toute utilisation des services Unotif.
            </p>

            {/* 2. Champ d'application */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              2. Champ d'application
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La présente politique s'applique à l'ensemble des services fournis par Unotif, incluant notamment :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>le site internet,</li>
              <li>l'application SaaS,</li>
              <li>les interfaces de gestion de cartes digitales,</li>
              <li>les fonctionnalités de notifications,</li>
              <li>les services associés à Apple Wallet et Google Wallet.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Elle s'applique à la fois :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>aux Clients (utilisateurs directs de la plateforme),</li>
              <li>et aux utilisateurs finaux des Clients (ci-après les « Utilisateurs finaux »), lorsque Unotif agit en tant que sous-traitant.</li>
            </ul>

            {/* 3. Rôles et responsabilités */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              3. Rôles et responsabilités (Responsable de traitement / Sous-traitant)
            </h2>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              3.1 Clients Unotif
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Dans le cadre de l'utilisation de la plateforme Unotif, les Clients sont responsables du traitement des données personnelles de leurs propres utilisateurs finaux.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              3.2 Unotif
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif agit :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>en tant que Responsable de traitement pour les données liées aux comptes Clients,</li>
              <li>en tant que Sous-traitant pour les données des Utilisateurs finaux traitées pour le compte de ses Clients.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif ne détermine ni la finalité ni le contenu des données personnelles que les Clients choisissent de traiter via la plateforme.
            </p>

            {/* 4. Données collectées */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              4. Données collectées
            </h2>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              4.1 Données fournies directement par les Clients
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Lors de la création d'un compte ou de l'utilisation des services, Unotif peut collecter les données suivantes :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>nom et prénom,</li>
              <li>adresse email,</li>
              <li>nom de société ou d'organisation,</li>
              <li>informations de connexion (identifiants, mots de passe chiffrés),</li>
              <li>informations de facturation (traitées exclusivement par Stripe),</li>
              <li>échanges avec le support client.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif ne stocke aucune donnée bancaire.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              4.2 Données traitées pour le compte des Clients (Utilisateurs finaux)
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les Clients peuvent utiliser Unotif pour traiter certaines données de leurs propres utilisateurs finaux, telles que :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>identifiants internes,</li>
              <li>noms,</li>
              <li>adresses email,</li>
              <li>données nécessaires à la personnalisation des cartes ou notifications.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ces données sont fournies sous la seule responsabilité du Client.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif n'exploite jamais ces données à des fins commerciales propres.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              4.3 Données collectées automatiquement
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Lors de l'utilisation des services, certaines données techniques peuvent être collectées automatiquement :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>adresse IP,</li>
              <li>type de navigateur,</li>
              <li>système d'exploitation,</li>
              <li>logs de connexion,</li>
              <li>données d'usage et statistiques anonymisées,</li>
              <li>données de performance et de sécurité.</li>
            </ul>

            {/* 5. Finalités du traitement des données */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              5. Finalités du traitement des données
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les données collectées sont utilisées exclusivement pour les finalités suivantes :
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              5.1 Fourniture du service
            </h3>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>création et gestion des comptes,</li>
              <li>fonctionnement des cartes digitales,</li>
              <li>envoi de notifications,</li>
              <li>gestion des quotas et limitations,</li>
              <li>facturation et abonnements.</li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              5.2 Amélioration des services
            </h3>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>analyse d'usage,</li>
              <li>amélioration des fonctionnalités,</li>
              <li>optimisation de la performance et de la sécurité.</li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              5.3 Communication
            </h3>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>support client,</li>
              <li>communications techniques ou administratives,</li>
              <li>informations relatives au service.</li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              5.4 Obligations légales et sécurité
            </h3>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>prévention des abus et fraudes,</li>
              <li>respect des obligations légales,</li>
              <li>protection des droits et intérêts de Unotif.</li>
            </ul>

            {/* 6. Partage des données */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              6. Partage des données
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif ne vend aucune donnée personnelle.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les données peuvent être partagées uniquement dans les cas suivants :
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              6.1 Prestataires techniques
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif peut faire appel à des prestataires tiers pour :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>l'hébergement des données,</li>
              <li>le paiement (Stripe),</li>
              <li>l'envoi d'emails transactionnels,</li>
              <li>la surveillance de la sécurité.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ces prestataires agissent uniquement sur instruction de Unotif et sont soumis à des obligations strictes de confidentialité.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              6.2 Obligations légales
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif peut être amenée à divulguer certaines données si la loi l'exige ou sur demande légale des autorités compétentes.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              6.3 Opérations de restructuration
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              En cas de fusion, acquisition ou cession d'actifs, les données pourraient être transférées dans le respect des lois applicables.
            </p>

            {/* 7. Sécurité des données */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              7. Sécurité des données
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif met en œuvre des mesures techniques et organisationnelles conformes aux standards de l'industrie afin de protéger les données personnelles, notamment :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>chiffrement,</li>
              <li>contrôles d'accès,</li>
              <li>journalisation,</li>
              <li>isolation des environnements,</li>
              <li>surveillance proactive.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Toutefois, aucun système n'étant infaillible, Unotif ne peut garantir une sécurité absolue.
            </p>

            {/* 8. Durée de conservation */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              8. Durée de conservation
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les données sont conservées :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>pendant la durée strictement nécessaire à la fourniture des services,</li>
              <li>ou pour satisfaire aux obligations légales,</li>
              <li>ou pour résoudre des litiges.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les données inutiles ou obsolètes sont supprimées ou anonymisées.
            </p>

            {/* 9. Droits des utilisateurs */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              9. Droits des utilisateurs (RGPD & CCPA)
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Conformément aux réglementations applicables, les utilisateurs disposent notamment des droits suivants :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>droit d'accès,</li>
              <li>droit de rectification,</li>
              <li>droit à l'effacement,</li>
              <li>droit à la limitation du traitement,</li>
              <li>droit d'opposition,</li>
              <li>droit à la portabilité des données.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les Utilisateurs finaux doivent adresser leurs demandes directement au Client responsable du traitement.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les Clients peuvent exercer leurs droits en contactant Unotif à l'adresse indiquée ci-dessous.
            </p>

            {/* 10. Cookies et technologies similaires */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              10. Cookies et technologies similaires
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif utilise des cookies strictement nécessaires au fonctionnement du service ainsi que, le cas échéant, des cookies analytiques.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les utilisateurs peuvent configurer leur navigateur pour refuser les cookies, sous réserve de certaines limitations fonctionnelles.
            </p>

            {/* 11. Données des mineurs */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              11. Données des mineurs
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les services Unotif ne sont pas destinés aux personnes âgées de moins de 16 ans.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif ne collecte pas sciemment de données concernant des mineurs.
              Toute donnée collectée par erreur sera supprimée dès identification.
            </p>

            {/* 12. Modification de la politique */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              12. Modification de la politique
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif se réserve le droit de modifier la présente politique à tout moment.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les modifications prennent effet dès leur publication.
              Les utilisateurs sont invités à consulter régulièrement cette page.
            </p>

            {/* 13. Mentions légales */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              13. Mentions légales
            </h2>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              Éditeur du service
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              SEO FRENCH TOUCH LLC<br />
              Société de droit américain (Limited Liability Company)
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Adresse :<br />
              539 W. COMMERCE ST #6642<br />
              DALLAS, TX 75208<br />
              États-Unis
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Nom commercial : Unotif
            </p>

            {/* 14. Contact */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              14. Contact
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Pour toute question relative à la présente politique de confidentialité ou à la protection des données personnelles, vous pouvez contacter Unotif à l'adresse suivante :
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              support@unotif.com
            </p>

          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Privacy;
